// src/services/projects.ts
import axios from '../axiosInstance';

import type {
  Project,
  ProjectType,
  ProjectGroup,
  GetRenderingsResponse,
  CreateNewVersionPayload,
} from '../types/project';

/**
 * Fetch project groups.
 * @param includeEntireAccount true → "All Projects", false → "My Projects"
 */
export const getProjects = async (includeEntireAccount: boolean): Promise<ProjectGroup[]> => {
  const { data } = await axios.get<ProjectGroup[]>('/projects', {
    params: { includeEntireAccount },
  });
  return data;
};

// TODO: update once api becomes avail
export const updateActiveVersion = async (
  parentProjectId: string,
  projectId: string,
): Promise<void> => {
  // Dummy API call - replace with actual implementation
  console.log(`Updating active version for parent ${parentProjectId} to project ${projectId}`);
  return Promise.resolve();
};

/**
 * Update project owner.
 * PATCH /projects/{projectId}/owner
 */
export const updateProjectOwner = async (
  parentProjectId: string,
  newOwner: string,
): Promise<void> => {
  await axios.patch(`/projects/parent/${parentProjectId}/owner`, { newOwner });
};

/**
 * Toggle lock state for all versions under a parent project.
 * POST /projects/parent/{parentProjectId}/toggle-lock
 */
export const toggleProjectLock = async (parentProjectId: string): Promise<void> => {
  await axios.patch(`/projects/parent/${parentProjectId}/toggle-lock`);
};

export const deleteProject = async (projectId: string): Promise<void> => {
  await axios.delete(`/projects/${projectId}`);
};

export const deleteParentProject = async (parentProjectId: string): Promise<void> => {
  await axios.delete(`/projects/parent/${parentProjectId}`);
};

/**
 * Update project details (name, versionName, customer).
 * PATCH /projects/{projectId}
 */
export const updateProjectDetails = async (
  projectId: string,
  details: {
    name?: string;
    versionName?: string;
    customer?: string;
  },
): Promise<void> => {
  await axios.patch(`/projects/${projectId}`, details);
};

/**
 * Create a new project
 * POST /projects
 */
export const createProject = async (payload: {
  name?: string;
  versionName?: string;
  projectType: ProjectType;
  postMeasure?: boolean;
  customer?: string;
  productLine: string;
  projectScope?: string;
  projectScopeCustom?: string;
  styleInfo?: any;
  isMultiUnit: boolean;
  unitName?: string;
  parentProjectId?: string | null;
  salesforceId?: string | null;
}): Promise<{ projectId: string; parentProjectId: string | null }> => {
  const url = payload.parentProjectId
    ? `/projects?parentProjectId=${payload.parentProjectId}`
    : '/projects';

  if (payload.productLine === 'garage' && !payload.styleInfo) {
    payload.styleInfo = {
      color: 'silverGrey',
      drawerColor: 'silverGrey',
      doorColor: 'silverGrey',
      handleColor: 'chrome',
      handleStyle: 'tBar',
      rodColor: 'chrome',
      drawerStyle: 'slab',
      doorStyle: 'slab',
    };
  }

  const { parentProjectId, ...bodyPayload } = payload;

  const { data } = await axios.post<{ projectId: string; parentProjectId: string }>(
    url,
    bodyPayload,
  );
  return data;
};

/**
 * Get presigned URL for blueprint upload
 * @param projectId - The project ID to upload blueprint for
 * @returns Promise with url
 */
export const getPresignedUrl = async (projectId: string): Promise<{ url: string }> => {
  const { data } = await axios.get(`/projects/${projectId}/blueprint-presigned-url`);
  return data;
};

/**
 * Get project details by ID
 * @param projectId - The project ID to fetch
 * @returns Promise with project details
 */
export const getProject = async (projectId: string): Promise<Project> => {
  const { data } = await axios.get<Project>(`/projects/${projectId}`);
  return data;
};

export const getParentProject = async (projectId: string): Promise<Project[]> => {
  const { data } = await axios.get<Project[]>(`/projects/parent/${projectId}`);
  return data;
};

/**
 * Create a new version of a project
 * @param projectId - The parent project ID
 * @param payload - The new version details
 * @returns Promise with new project ID
 */
export const createNewVersion = async (
  projectId: string,
  payload: CreateNewVersionPayload,
): Promise<{ projectId: string }> => {
  const { data } = await axios.post<{ projectId: string }>(
    `/projects/${projectId}/new-version`,
    payload,
  );
  return data;
};

export const unsubmitTakeoff = async (projectId: string, fullReset: boolean): Promise<void> => {
  await axios.post(`/projects/${projectId}/unsubmit-takeoff?fullReset=${fullReset}`);
};

export const triggerAutoRender = async (projectId: string): Promise<void> => {
  await axios.post(`/projects/${projectId}/auto-render`);
};

export const getRenderings = async (projectId: string): Promise<GetRenderingsResponse> => {
  const { data } = await axios.get(`/quotes/${projectId}/rendering`);
  return data;
};

export const addToCart = async (projectId: string): Promise<string> => {
  const quote = await axios.post(`/quotes/${projectId}`);
  console.log(quote.data);
  return quote.data.cartUrl;
};

export const createDraftOrder = async (projectId: string): Promise<string> => {
  const draftOrder = await axios.post(`/quotes/${projectId}/draft-order`);
  return draftOrder.data.link;
};

export const duplicateProject = async (projectId: string): Promise<void> => {
  await axios.post(`/projects/${projectId}/duplicate`);
};

export const updateSfId = async (parentProjectId: string, sfId: string): Promise<void> => {
  await axios.patch(`/projects/parent/${parentProjectId}/sf-id`, { salesforceID: sfId });
};

export const transferProjectToAccount = async (
  parentProjectId: string,
  newAccountId: string,
  newOwner: string,
): Promise<void> => {
  await axios.patch(`/projects/parent/${parentProjectId}/account`, { newAccountId, newOwner });
};